import React, { useCallback, useEffect, useState } from "react";
import api, { brl, fmtDate } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { BaixaModal } from "@/pages/Installments";
import { useRefreshListener } from "@/lib/refresh";
import { MetaSemanal } from "@/components/MetaSemanal";
import { Button } from "@/components/ui/button";
import { CalendarClock, AlertTriangle, Wallet, Loader2, BadgeDollarSign, Target, Handshake } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function CobrancaDiaria() {
  const navigate = useNavigate();
  const today = new Date().toLocaleDateString("en-CA");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);

  const [meta, setMeta] = useState(null);
  const [week, setWeek] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data }, m, w] = await Promise.all([api.get(`/installments?ate=${today}&encargos=1`), api.get("/collection/today"), api.get("/collection/week")]);
    setItems(data);
    setMeta(m.data);
    setWeek(w.data);
    setLoading(false);
  }, [today]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(load, 60000); const v = () => document.visibilityState === "visible" && load(); document.addEventListener("visibilitychange", v); return () => { clearInterval(t); document.removeEventListener("visibilitychange", v); }; }, [load]);
  useRefreshListener(load);

  const venceHoje = items.filter((p) => p.vencimento === today);
  const atrasadas = items.filter((p) => p.vencimento < today);
  const acordosAtraso = atrasadas.filter((p) => p.acordo);
  const devido = (p) => (p.encargos ? Number(p.encargos.total_devido) : Number(p.valor || 0) - Number(p.valor_pago || 0));
  const totalHoje = venceHoje.reduce((s, p) => s + devido(p), 0);
  const totalAtraso = atrasadas.reduce((s, p) => s + devido(p), 0);

  const Section = ({ title, rows, icon: Icon, tone, testid }) => (
    <div className="trt-card overflow-hidden" data-testid={testid}>
      <div className={`px-4 py-3 font-bold border-b flex items-center gap-2 ${tone}`}><Icon className="w-4 h-4" /> {title} ({rows.length})</div>
      {rows.length === 0 ? <div className="py-8 text-center text-slate-400 text-sm">Nada aqui 🎉</div> :
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} className="border-t hover:bg-slate-50" data-testid={`cob-row-${p.id}`}>
                <td className="px-4 py-3 font-medium">
                  <button className="hover:underline text-left" onClick={() => navigate(`/clientes/${p.cliente_id}`)} data-testid={`cob-cliente-${p.id}`}>{p.cliente_nome}</button>
                  {p.acordo && <span className="ml-2 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200 font-semibold" data-testid={`cob-acordo-tag-${p.id}`}><Handshake className="w-3 h-3" /> ACORDO {fmtDate(p.acordo.created_at)}</span>}
                  <div className="text-xs text-slate-400">{p.acordo ? `Parcela ${p.numero}/${p.acordo.num_parcelas} do acordo` : `Parcela #${p.numero}`}{p.contrato_numero && ` • contrato ${p.contrato_numero}`} • vence {fmtDate(p.vencimento)}{p.encargos?.atrasada && <span className="text-red-500"> • {p.encargos.dias_atraso} dia(s) de atraso</span>}</div>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <div className="font-bold" data-testid={`cob-valor-${p.id}`}>{brl(devido(p))}</div>
                  {p.encargos && (p.encargos.juros > 0 || p.encargos.multa > 0 || Number(p.valor_pago) > 0) && (
                    <div className="text-[11px] text-slate-500">original {brl(p.valor)}{Number(p.valor_pago) > 0 && ` • pago ${brl(p.valor_pago)}`}{p.encargos.multa > 0 && ` • multa ${brl(p.encargos.multa)}`}{p.encargos.juros > 0 && ` • juros ${brl(p.encargos.juros)}`}</div>
                  )}
                </td>
                <td className="px-4 py-3"><StatusBadge type="inst" status={p.status} vencimento={p.vencimento} /></td>
                <td className="px-4 py-3 text-right">
                  <Button size="sm" onClick={() => setSel(p)} data-testid={`cob-baixa-${p.id}`}
                    className="bg-gold hover:bg-[hsl(var(--gold-hover))] text-black font-semibold gap-1"><BadgeDollarSign className="w-4 h-4" /> DAR BAIXA</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>}
    </div>
  );

  return (
    <div className="space-y-5 fade-up" data-testid="cobranca-page">
      <div><h1 className="text-2xl font-extrabold tracking-tight">Cobrança do Dia</h1><p className="text-slate-500 text-sm">Parcelas a receber hoje e em atraso — {fmtDate(today)}.</p></div>

      {meta && (
        <div className="bg-onyx rounded-xl p-5 gold-border space-y-3" data-testid="meta-dia">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 text-white font-bold"><Target className="w-5 h-5 gold-text" /> Meta do Dia</div>
            <div className="text-xs text-slate-400">{meta.pagamentos} pagamento(s) hoje • {meta.percentual}% da meta{meta.meta_fixa > 0 && <span className="ml-1 gold-text" data-testid="meta-fixa-tag">(meta fixa {brl(meta.meta_fixa)})</span>}</div>
          </div>
          <div className="h-3 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-gold transition-[width] duration-700" style={{ width: `${meta.percentual}%` }} data-testid="meta-progress" />
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div><div className="text-slate-400 text-[11px] uppercase tracking-wider">Recebido hoje</div>
              <div className="text-emerald-400 font-extrabold mono text-lg" data-testid="meta-recebido">{brl(meta.recebido)}</div></div>
            <div><div className="text-slate-400 text-[11px] uppercase tracking-wider">Falta receber</div>
              <div className="text-amber-300 font-extrabold mono text-lg" data-testid="meta-falta">{brl(meta.falta)}</div></div>
            <div><div className="text-slate-400 text-[11px] uppercase tracking-wider">{meta.meta_fixa > 0 ? "Meta do dia" : "Previsto hoje"}</div>
              <div className="text-white font-extrabold mono text-lg" data-testid="meta-previsto">{brl(meta.meta)}</div></div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-onyx rounded-xl p-5 gold-border">
          <Wallet className="w-5 h-5 gold-text mb-2" />
          <div className="text-slate-400 text-xs uppercase tracking-wider">A receber hoje</div>
          <div className="text-white text-2xl font-extrabold mono mt-1" data-testid="cob-total-hoje">{brl(totalHoje)}</div>
        </div>
        <div className="trt-card p-5">
          <CalendarClock className="w-5 h-5 text-amber-600 mb-2" />
          <div className="text-slate-500 text-xs uppercase tracking-wider">Parcelas de hoje</div>
          <div className="text-2xl font-extrabold mt-1">{venceHoje.length}</div>
        </div>
        <div className="trt-card p-5 border-red-200">
          <AlertTriangle className="w-5 h-5 text-red-500 mb-2" />
          <div className="text-slate-500 text-xs uppercase tracking-wider">Total atrasado</div>
          <div className="text-2xl font-extrabold mt-1 text-red-600 mono">{brl(totalAtraso)}</div>
        </div>
      </div>

      <MetaSemanal week={week} />

      {loading ? <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin gold-text mx-auto" /></div> : (
        <>
          <Section title="Vencendo hoje" rows={venceHoje} icon={CalendarClock} tone="text-amber-700" testid="cob-sec-hoje" />
          <Section title="Em atraso" rows={atrasadas.filter((p) => !p.acordo)} icon={AlertTriangle} tone="text-red-600" testid="cob-sec-atraso" />
          <Section title="Acordos em atraso" rows={acordosAtraso} icon={Handshake} tone="text-violet-700" testid="cob-sec-acordos" />
        </>
      )}

      <BaixaModal inst={sel} onClose={() => setSel(null)} onDone={load} />
    </div>
  );
}
